import { Component } from '@theme/component';

/**
 * FAQ filtering: a search field and a row of category chips.
 *
 * Progressive by construction. Every question and every answer is server
 * rendered and reachable with no JavaScript at all - this only hides what does
 * not match. The controls are rendered `hidden` and revealed on connect, so a
 * visitor whose script never runs is never shown a search box that does nothing.
 *
 * Matching runs against `data-faq-text` on each <details>, which the section
 * writes from the question and the answer together, lower-cased at render time.
 * That keeps the work per keystroke to a substring test.
 */
class AsxFaqSearch extends Component {
  requiredRefs = ['field'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.hidden = false;

    const field = this.refs.field;
    if (field instanceof HTMLInputElement) {
      field.addEventListener('input', this.#onInput, { signal: this.#abortController.signal });
      // A search field inside a form would submit on Enter and reload the page.
      field.addEventListener(
        'keydown',
        (event) => {
          if (event.key === 'Enter') event.preventDefault();
          if (event.key === 'Escape') {
            field.value = '';
            this.#apply();
          }
        },
        { signal: this.#abortController.signal }
      );
    }

    // A question linked to directly should be open when the reader arrives.
    this.#openFromHash();
    window.addEventListener('hashchange', this.#openFromHash, { signal: this.#abortController.signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #debounce;

  #onInput = () => {
    clearTimeout(this.#debounce);
    this.#debounce = setTimeout(() => this.#apply(), 120);
  };

  /** Filters by the active chip. Bound declaratively from the markup. */
  filter = (event) => {
    const chip = event.target;
    if (!(chip instanceof HTMLElement)) return;

    for (const other of this.querySelectorAll('[data-faq-chip]')) {
      other.setAttribute('aria-pressed', other === chip ? 'true' : 'false');
    }

    this.#apply();
  };

  get #category() {
    const active = this.querySelector('[data-faq-chip][aria-pressed="true"]');
    return active instanceof HTMLElement ? active.dataset.faqChip || '' : '';
  }

  #apply() {
    const field = this.refs.field;
    const term = field instanceof HTMLInputElement ? field.value.trim().toLowerCase() : '';
    const category = this.#category;

    let total = 0;

    for (const group of document.querySelectorAll('[data-faq-group]')) {
      const inCategory = !category || (group.dataset.faqCategory || '') === category;
      let shown = 0;

      for (const item of group.querySelectorAll('[data-faq-item]')) {
        const haystack = item.getAttribute('data-faq-text') || '';
        const match = inCategory && (!term || haystack.indexOf(term) !== -1);
        item.hidden = !match;
        if (match) shown++;
        // Collapse anything filtered away, so clearing the field does not
        // leave a trail of answers open behind the reader.
        if (!match && item instanceof HTMLDetailsElement) item.open = false;
      }

      group.hidden = shown === 0;
      total += shown;
    }

    const empty = this.refs.empty;
    if (empty instanceof HTMLElement) empty.hidden = total > 0;
  }

  #openFromHash = () => {
    const id = window.location.hash.slice(1);
    if (!id) return;

    const target = document.getElementById(id);
    if (target instanceof HTMLDetailsElement) {
      target.open = true;
      target.scrollIntoView({ block: 'center', behavior: 'auto' });
    }
  };
}

if (!customElements.get('asx-faq-search')) {
  customElements.define('asx-faq-search', AsxFaqSearch);
}

export default AsxFaqSearch;
